import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { toISO, today } from "../../lib/dateUtils";
import { formatUSD } from "../../lib/formatters";

const norm = (s) => (s || "").trim().toLowerCase();

export default function PaymentForm({ open, onClose, onSubmit, clients, offers, initial }) {
  const [form, setForm] = useState(() => initial || {
    clientEmail: "",
    clientId: clients[0]?.id || "",
    offerId: "",
    amount: "",
    amountClientBudget: "",  // optional pass-through portion (SEO backlinks model)
    paidDate: toISO(today()),
    bookedDate: toISO(today()),
    note: "",
  });
  const [errors, setErrors] = useState({});
  const [splitOpen, setSplitOpen] = useState(
    initial ? Number(initial.amountClientBudget) > 0 : false
  );

  useEffect(() => {
    if (initial) setForm(initial);
  }, [initial]);

  // When email is entered & matches a known client, auto-select & lock the client dropdown
  const emailMatchedClient = useMemo(() => {
    const e = norm(form.clientEmail);
    if (!e) return null;
    return clients.find((c) => norm(c.email) === e) || null;
  }, [form.clientEmail, clients]);

  useEffect(() => {
    if (emailMatchedClient && form.clientId !== emailMatchedClient.id) {
      setForm((f) => ({ ...f, clientId: emailMatchedClient.id }));
    }
  }, [emailMatchedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const total = Number(form.amount) || 0;
  const clientBudget = splitOpen ? (Number(form.amountClientBudget) || 0) : 0;
  const revenue = Math.max(0, total - clientBudget);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.clientId) errs.clientId = "Pick a client";
    if (!form.offerId) errs.offerId = "Pick an offer";
    if (!(total > 0)) errs.amount = "Must be > 0";
    if (!form.paidDate) errs.paidDate = "Required";
    if (splitOpen && clientBudget > total) errs.amountClientBudget = "Cannot exceed total";
    if (splitOpen && clientBudget <= 0) errs.amountClientBudget = "Must be > 0 (or turn off split)";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const bookedDate = form.bookedDate || form.paidDate;
    onSubmit({
      clientId: form.clientId,
      offerId: form.offerId,
      amount: total,
      amountRevenue: revenue,
      amountClientBudget: clientBudget,
      paidDate: form.paidDate,
      bookedDate,
      dueDate: form.paidDate,
      status: "paid",
      note: form.note,
    });
    onClose();
  };

  const clientLocked = !!emailMatchedClient;

  return (
    <Modal open={open} onClose={onClose} title="Record payment">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          label="Client email (optional — auto-matches client)"
          type="email"
          value={form.clientEmail}
          onChange={(e) => update("clientEmail", e.target.value)}
          placeholder="leave empty to pick client manually"
        />
        <div>
          <span className="block text-xs font-medium text-brand-slate mb-1">Client *</span>
          <select
            value={form.clientId}
            onChange={(e) => update("clientId", e.target.value)}
            disabled={clientLocked}
            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] ${clientLocked ? "opacity-60 cursor-not-allowed" : ""} ${errors.clientId ? "border-red-400" : "border-gray-200"}`}
          >
            <option value="">Select client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ""}</option>)}
          </select>
          {clientLocked && (
            <p className="text-xs text-emerald-700 mt-1">Auto-matched by email to {emailMatchedClient.name}</p>
          )}
          {errors.clientId && <p className="text-xs text-red-600 mt-1">{errors.clientId}</p>}
        </div>
        <Select label="Offer *" value={form.offerId} onChange={(e) => update("offerId", e.target.value)} error={errors.offerId}>
          <option value="">Select offer</option>
          {offers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Amount (USD) *" type="number" value={form.amount} onChange={(e) => update("amount", e.target.value)} error={errors.amount} />
          <Input label="Paid date *" type="date" value={form.paidDate} onChange={(e) => update("paidDate", e.target.value)} error={errors.paidDate} />
        </div>

        {/* Pass-through split — for SEO backlinks etc. where part of the payment
            is held to spend on the client's behalf later. Off by default. */}
        <div className={`rounded-lg border ${splitOpen ? "border-purple-200 bg-purple-50/40 dark:bg-purple-900/10" : "border-gray-200 dark:border-[var(--border-soft)]"} p-3`}>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={splitOpen}
              onChange={(e) => setSplitOpen(e.target.checked)}
              className="mt-0.5 cursor-pointer"
            />
            <span>
              <span className="block text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">Split this payment (pass-through)</span>
              <span className="block text-[11px] text-brand-slate">
                Use when part of the payment is held to spend on the client (e.g. SEO backlinks). Only the agency-revenue portion counts in P&amp;L; the held portion becomes a client liability.
              </span>
            </span>
          </label>
          {splitOpen && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Input
                label="Client budget held (USD) *"
                type="number"
                value={form.amountClientBudget}
                onChange={(e) => update("amountClientBudget", e.target.value)}
                error={errors.amountClientBudget}
                placeholder="e.g. 7000"
              />
              <div className="flex flex-col justify-end">
                <span className="block text-xs font-medium text-brand-slate mb-1">Agency revenue (auto)</span>
                <div className={`px-3 py-2 rounded-lg text-sm font-semibold ${revenue > 0 ? "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" : "text-brand-slate bg-gray-50 dark:bg-[var(--bg-soft)]"}`}>
                  {formatUSD(revenue)}
                </div>
                <span className="block text-[10px] text-brand-slate mt-1">
                  total − client budget = recognized revenue
                </span>
              </div>
            </div>
          )}
        </div>

        <Input label="Booked date (revenue recognition)" type="date" value={form.bookedDate} onChange={(e) => update("bookedDate", e.target.value)} />
        <Input label="Note" value={form.note} onChange={(e) => update("note", e.target.value)} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">Record payment</Button>
        </div>
      </form>
    </Modal>
  );
}
